import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../context/ChatContext';
import ChatMessage from './ChatMessage';
import StreamingIndicator from './StreamingIndicator';
import ToolExecutionBadge from './ToolExecutionBadge';
import './ChatMessages.css';

/**
 * Messages container component
 * Displays all chat messages with auto-scroll functionality
 */
function ChatMessages() {
  const { t } = useTranslation();
  const {
    messages,
    isStreaming,
    streamingMessage,
    toolExecutions,
    progressMessage,
  } = useChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  return (
    <div className="chat-messages">
      <div className="messages-container">
        {messages.length === 0 && !isStreaming && (
          <div className="empty-state">
            <h2>{t('chat.select_session')}</h2>
            <p>{t('chat.type_message')}</p>
          </div>
        )}

        {messages.map((message, index) => (
          <ChatMessage
            key={`${message.timestamp}-${index}`}
            message={message}
          />
        ))}

        {/* Show streaming message */}
        {isStreaming && (
          <div className="message assistant streaming">
            <div className="message-avatar">AI</div>
            <div className="message-content">
              {/* Progress status */}
              {progressMessage && (
                <div className="progress-status">
                  <span className="progress-icon">⏳</span>
                  <span className="progress-text">{progressMessage}</span>
                </div>
              )}

              {/* Tool executions */}
              {toolExecutions.length > 0 && (
                <div className="tool-executions">
                  {toolExecutions.map((tool) => (
                    <ToolExecutionBadge key={tool.id} execution={tool} />
                  ))}
                </div>
              )}

              {/* Show typing indicator while processing */}
              <div className="typing-wrapper">
                <StreamingIndicator />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

export default ChatMessages;
